import { updateUIState, assignStartingNumbers, checkForbiddenPairing } from '../app.js';
import { state, saveToLocalStorage, getActiveSettings, closeRegistrationOnFirstPairing, getTeams, defaultSeedColor } from '../state.js';
import { formatPlayerName, escapeHtml } from '../utils/formatting.js';
import { renderStandings } from './standings.js';
import {
    generateNextRound as generateSwissPairings,
    preload as preloadPairingEngine
} from '../services/pairing-engine.js';
import { generateTeamRound } from '../services/team-pairing-engine.js';
import { roundWhenLabel, syncScheduleButtons } from './schedule.js';
import { roundIsPublished } from '../utils/round-publication.js';
import { editedBoardResult } from '../utils/edited-pairings.js';

// Bump the structure revision after a STRUCTURAL round change (generate round,
// undo round, edit pairings). The multi-arbiter merge uses this to tell "this
// arbiter changed the round structure" apart from "this arbiter has a stale
// copy" — e.g. an undone round must not be resurrected by a co-arbiter's write,
// and a freshly generated round must not be deleted by one. Result entry alone
// does NOT bump it (results merge per board).
function bumpStructureRev() {
    const t = state.activeTournament;
    if (!t) return;
    if (t.groups && state.activeGroupId) {
        const g = t.groups.find(g => String(g.id) === String(state.activeGroupId));
        if (g) g._structureRev = (g._structureRev || 0) + 1;
    }
    t._structureRev = (t._structureRev || 0) + 1;
}

// ---------------------------------------------------------------------------
// PUBLISHING PAIRINGS
//
// A tournament either shows a round the moment it is paired (pairingPublish
// 'auto', the default and what every tournament did before this setting) or
// holds it back until the arbiter presses Publish on this page ('manual').
//
// Manual mode stamps `published: false` on the round as it is generated; the
// button flips it. Everything else reads the flag through roundIsPublished(),
// which treats a round with no flag at all as published — so no tournament
// created before this existed suddenly goes dark.
//
// The hiding itself happens where the spectator's copy is loaded
// (stripUnpublishedRounds in js/app.js), not here: an unpublished round then
// does not exist for them at all, so their standings, crosstable and pairings
// all agree with one another. The arbiter always sees every round.
//
// Worth saying plainly: this decides what the APP shows. The round document is
// still in the database and a public tournament's documents are world-readable,
// so this is a curtain, not a lock.
// ---------------------------------------------------------------------------

export function pairingsHeldBack() {
    const t = state.activeTournament;
    return !!t && t.pairingPublish === 'manual';
}

// Publish (or un-publish) the round currently on screen.
async function setViewedRoundPublished(published) {
    const round = state.rounds[state.viewedRoundIndex];
    if (!round) return;
    if (!published) {
        const ok = await window.Dialog.confirm(
            tr('Round {n} will disappear for spectators until you publish it again. Results you have already entered go with it.', { n: round.roundNo }),
            tr('Unpublish Round'),
            tr('Unpublish'),
            true
        );
        if (!ok) return;
    }
    round.published = !!published;
    // Publishing changes what the round document says, and a co-arbiter whose copy
    // still says otherwise must not win the merge and hide it again.
    bumpStructureRev();
    saveToLocalStorage();
    renderCurrentRound();
    // Reaches the live broadcast too (through renderLive): a round nobody may see
    // has no public boards either, and publishing hands over every move the bridge
    // has already recorded. See bridgeRoundIsPublic() in js/ui/broadcast-arbiter.js.
    updateUIState();
}

// What the Publish Pairings button does. Wired in js/app.js.
export async function togglePublishViewedRound() {
    const round = state.rounds[state.viewedRoundIndex];
    if (!round) return;
    await setViewedRoundPublished(!roundIsPublished(round));
}

// Stamp every result write (including clearing back to "---") with a
// timestamp so the multi-arbiter merge can tell which side's result is
// actually newer, instead of guessing from "is it empty" — that guess broke
// down for an explicit clear, which looks identical to "never entered" and
// so could be silently overwritten by a stale non-empty remote copy.
function setResult(pairing, value) {
    pairing.result = value;
    pairing.resultTs = Date.now();
}


// DOM Elements
const pairingsContainer = document.getElementById('pairings-container');
const currentRoundTitle = document.getElementById('current-round-title');
const btnPrevRound = document.getElementById('btn-prev-round');
const btnNextRound = document.getElementById('btn-next-round');
const btnGenerateRound = document.getElementById('btn-generate-round');
const btnRegisterResults = document.getElementById('btn-register-results');
const btnUndoRound = document.getElementById('btn-undo-round');
const btnEditPairings = document.getElementById('btn-edit-pairings');
const btnPublishPairings = document.getElementById('btn-publish-pairings');
const editPairingsModal = document.getElementById('edit-pairings-modal');
const editPairingsSearch = document.getElementById('edit-pairings-search');

let editPairingsState = [];
// `${white}|${black}` of each board the round had when Edit Pairings opened: a board
// still among them is unchanged (editedBoardResult).
let editPairingsOriginal = new Set();
let registerResultsState = [];
let selectedBoardIdx = -1;
let isRegisterResultsInitialized = false;

// Keyboard Hotkeys State & Defaults
const DEFAULT_HOTKEYS = {
    '1-0': '1',
    '0.5-0.5': '2',
    '0-1': '3',
    '+/-': '4',
    '-/-': '5',
    '-/+': '6',
    'delete': 'd'
};

let hotkeysState = { ...DEFAULT_HOTKEYS };
let tempHotkeys = {};
let recordingAction = null;

export function loadHotkeys() {
    try {
        const saved = localStorage.getItem('resultsHotkeys');
        if (saved) {
            const parsed = JSON.parse(saved);
            // Check if the saved configuration matches the exact old defaults
            const isOldDefault = 
                parsed['1-0'] === '1' &&
                parsed['+/-'] === '2' &&
                parsed['0.5-0.5'] === '3' &&
                parsed['-/-'] === '4' &&
                parsed['0-1'] === '5' &&
                parsed['-/+'] === '6' &&
                (parsed['delete'] === 'd' || parsed['delete'] === 'D');
            
            if (isOldDefault) {
                hotkeysState = { ...DEFAULT_HOTKEYS };
                localStorage.setItem('resultsHotkeys', JSON.stringify(DEFAULT_HOTKEYS));
            } else {
                hotkeysState = { ...DEFAULT_HOTKEYS, ...parsed };
            }
        }
    } catch (e) {
        console.error('Error loading hotkeys:', e);
    }
}

export function saveHotkeys() {
    localStorage.setItem('resultsHotkeys', JSON.stringify(hotkeysState));
}

export function updateHotkeyBadges() {
    document.querySelectorAll('.hotkey-badge').forEach(badge => {
        const action = badge.dataset.action;
        const key = hotkeysState[action];
        if (key) {
            badge.textContent = key.toUpperCase();
        }
    });
}

// Visual label helpers
const ACTION_LABELS = {
    '1-0': 'Win (1-0)',
    '+/-': 'WO Win (+/-)',
    '0.5-0.5': 'Draw (½-½)',
    '-/-': 'Double WO (-/-)',
    '0-1': 'Loss (0-1)',
    '-/+': 'WO Loss (-/+)',
    'delete': 'Delete Result'
};

function renderHotkeysList() {
    const list = document.getElementById('hotkeys-list');
    if (!list) return;

    list.innerHTML = Object.entries(ACTION_LABELS).map(([action, label]) => {
        const key = tempHotkeys[action];
        const isRecording = recordingAction === action;
        
        const btnStyle = isRecording
            ? `border: 2px solid var(--primary); background: rgba(59, 130, 246, 0.15); box-shadow: 0 0 8px rgba(59, 130, 246, 0.3); font-weight: 800; color: var(--primary); animation: pulse-highlight 1.5s infinite;`
            : `border: 1px solid var(--border-color); background: var(--surface-tint); color: var(--text-main); font-weight: 700;`;

        return `
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.75rem; border-radius: 8px; border: 1px solid var(--border-color); background: var(--surface-tint-faint);">
                <div style="font-size: 0.85rem; font-weight: 600; color: var(--text-main);">${tr(label)}</div>
                <button type="button" class="btn-hotkey-record" data-action="${action}" style="margin: 0; min-width: 110px; height: 32px; font-size: 0.75rem; border-radius: 6px; cursor: pointer; transition: all 0.2s; text-transform: uppercase; ${btnStyle}">
                    ${isRecording ? tr('Press key...') : (key ? key : tr('NONE'))}
                </button>
            </div>
        `;
    }).join('');

    // Attach click listeners to key buttons to start recording
    list.querySelectorAll('.btn-hotkey-record').forEach(button => {
        button.addEventListener('click', () => {
            const action = button.dataset.action;
            if (recordingAction === action) {
                recordingAction = null; // Toggle off
            } else {
                recordingAction = action;
            }
            renderHotkeysList();
        });
    });
}

function openEditHotkeysModal() {
    tempHotkeys = { ...hotkeysState };
    recordingAction = null;
    renderHotkeysList();

    const modal = document.getElementById('edit-hotkeys-modal');
    if (modal) {
        modal.classList.add('active');
    }
}

// Load hotkeys immediately on script evaluation
loadHotkeys();

// 1. Global keydown listener for keyboard hotkey registration
window.addEventListener('keydown', (e) => {
    const modal = document.getElementById('register-results-modal');
    if (!modal || !modal.classList.contains('active')) return;

    const hotkeysModal = document.getElementById('edit-hotkeys-modal');
    if (hotkeysModal && hotkeysModal.classList.contains('active')) return;

    const activeEl = document.activeElement;
    if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT')) {
        return;
    }

    const pressedKey = e.key.toLowerCase();
    let matchedAction = null;
    for (const [action, key] of Object.entries(hotkeysState)) {
        if (key.toLowerCase() === pressedKey) {
            matchedAction = action;
            break;
        }
    }

    if (!matchedAction) return;

    e.preventDefault();

    if (matchedAction === 'delete') {
        if (selectedBoardIdx >= 0 && selectedBoardIdx < registerResultsState.length) {
            setResult(registerResultsState[selectedBoardIdx], '');
            renderRegisterResults();
        }
    } else {
        if (selectedBoardIdx >= 0 && selectedBoardIdx < registerResultsState.length) {
            setResult(registerResultsState[selectedBoardIdx], matchedAction);
            selectNextAvailableBoard();
            renderRegisterResults();
        }
    }
});

// 2. Capture keyboard key inside key recording mode in Edit Hotkeys
window.addEventListener('keydown', async (e) => {
    const hotkeysModal = document.getElementById('edit-hotkeys-modal');
    if (!hotkeysModal || !hotkeysModal.classList.contains('active')) return;
    if (!recordingAction) return;

    e.preventDefault();
    e.stopPropagation();

    if (e.key === 'Escape') {
        recordingAction = null;
        renderHotkeysList();
        return;
    }

    const key = e.key;
    let keyName = key;
    if (keyName === ' ') {
        keyName = 'Space';
    }

    // Check if key is already assigned to another action (case-insensitive)
    const duplicateAction = Object.entries(tempHotkeys).find(([action, k]) => {
        return action !== recordingAction && k.toLowerCase() === keyName.toLowerCase();
    });

    if (duplicateAction) {
        const actionLabel = ACTION_LABELS[duplicateAction[0]];
        await window.Dialog.warn(tr('The key "{key}" is already assigned to "{action}". Please choose a different key.', { key: keyName.toUpperCase(), action: actionLabel }), tr('Key Conflict'));
        return;
    }

    tempHotkeys[recordingAction] = keyName;
    recordingAction = null;
    renderHotkeysList();
});

// ---------------------------------------------------------------------------
// Round robin schedule preview
// In a round robin every round is predetermined by the Berger table, so the
// pairings tab lets Prev/Next browse the WHOLE schedule. Rounds beyond
// state.rounds are rendered as read-only previews computed from the pinned
// pairing order (`rrPairingOrder`) — the same field the generator uses.
// ---------------------------------------------------------------------------

function rrScheduleField() {
    const settings = getActiveSettings();
    if (settings.format !== 'round-robin' && settings.format !== 'double-round-robin') return null;
    const roster = state.players.filter(p => p.startNo);
    if (roster.length < 2) return null;

    const t = state.activeTournament;
    let store = t;
    if (t && t.groups && state.activeGroupId) {
        const g = t.groups.find(gr => String(gr.id) === String(state.activeGroupId));
        if (g) store = g;
    }

    const bySnr = (a, b) => (a.startNo || 999) - (b.startNo || 999);
    let orderIds = (store && Array.isArray(store.rrPairingOrder))
        ? store.rrPairingOrder.filter(id => roster.some(p => p.id === id))
        : [];
    const extras = roster.filter(p => !orderIds.includes(p.id)).sort(bySnr);
    orderIds = [...orderIds, ...extras.map(p => p.id)];
    return orderIds.map(id => state.players.find(p => p.id === id));
}

// Rounds browsable in the pairings view: the generated rounds, plus — for a
// round robin — the not-yet-generated remainder of the Berger schedule.
export function getViewableRoundCount() {
    let total = state.rounds.length;
    const field = rrScheduleField();
    if (field) {
        const settings = getActiveSettings();
        const cycle = field.length % 2 === 0 ? field.length - 1 : field.length;
        const maxR = settings.format === 'double-round-robin' ? cycle * 2 : cycle;
        const configured = parseInt(settings.roundsCount) || maxR;
        total = Math.max(total, Math.min(configured, maxR));
    }
    return total;
}

// The round to display at an index: a real generated round, or a Berger-table
// preview. A preview shows the scheduled pairings; if history has deviated
// from the schedule the actual generated round may differ (generateNextRound
// runs the rematch check), which is why previews are marked as such.
function getViewedRound(roundIndex) {
    if (roundIndex < state.rounds.length) {
        return { round: state.rounds[roundIndex], isPreview: false };
    }
    const field = rrScheduleField();
    if (!field || roundIndex >= getViewableRoundCount()) return { round: null, isPreview: true };

    const pairings = RoundRobinEngine.generatePairings(field, roundIndex + 1, 0);
    pairings.sort((a, b) => {
        const aIsWO = a.white === 'WO' || a.black === 'WO';
        const bIsWO = b.white === 'WO' || b.black === 'WO';
        return (aIsWO ? 1 : 0) - (bIsWO ? 1 : 0);
    });
    return { round: { roundNo: roundIndex + 1, pairings }, isPreview: true };
}

// Publish / Unpublish in the page header: the arbiter only, a real generated
// round only, and only while this tournament holds its pairings back.
function syncPublishButton(round, isPreview) {
    if (!btnPublishPairings) return;
    const show = !state.isSpectatorMode
        && !isPreview
        && !!round
        && state.viewedRoundIndex < state.rounds.length
        && pairingsHeldBack();
    btnPublishPairings.style.display = show ? '' : 'none';
    if (!show) return;

    const published = roundIsPublished(round);
    btnPublishPairings.className = (published ? 'btn-outline' : 'btn-primary') + ' arbiter-only';
    btnPublishPairings.title = published
        ? tr('Hide this round from spectators again')
        : tr('Let spectators see this round');
    const icon = btnPublishPairings.querySelector('i');
    if (icon) icon.className = published ? 'ri-eye-off-line' : 'ri-eye-line';
    const label = document.getElementById('publish-pairings-label');
    if (label) label.textContent = published ? tr('Unpublish') : tr('Publish Pairings');
}

// ---------------------------------------------------------------------------
// Pairing rows
//
// A team round stores BOTH shapes: `round.matches` is the team-versus-team pairing,
// and `round.pairings` is the flat list of board games it expands into. The table
// below is driven by the flat list either way — which is exactly why result entry,
// the fullscreen view and the individual standings needed no team-specific code at
// all. Grouping is presentation: the same rows, with a header in front of each match.
//
// `idx` is always the index into round.pairings, never a display position. The result
// <select> carries it and the change handler writes straight back to that entry, so
// reordering rows for display must never renumber it.
// ---------------------------------------------------------------------------

const PAIRING_RESULT_OPTIONS = [
    ['', '---'], ['1-0', '1 - 0'], ['0.5-0.5', '½ - ½'], ['0-1', '0 - 1'],
    ['+/-', '+ / -'], ['-/+', '- / +'], ['-/-', '- / -']
];

function resultCellHtml(p, idx, readOnly) {
    if (readOnly) {
        const labels = Object.fromEntries(PAIRING_RESULT_OPTIONS.slice(1));
        const shown = labels[p.result] || p.result || '—';
        return `<div style="padding: 0.2rem 0.75rem; background: var(--surface-tint); border: 1px solid var(--border-color); border-radius: 6px; font-weight: 700; color: var(--text-main); font-size: 0.9rem; min-width: 70px; text-align: center;">${shown}</div>`;
    }
    const options = PAIRING_RESULT_OPTIONS
        .map(([value, label]) => `<option value="${value}" ${(p.result || '') === value ? 'selected' : ''}>${label}</option>`)
        .join('');
    return `<select class="pairing-result-select" data-index="${idx}" style="margin: 0; width: 90px; height: 28px; padding: 2px 5px; font-size: 0.85rem;">${options}</select>`;
}

// In an Arena every player plays for a team, and the pairing list says which: the arbiter
// sees at a glance that the bottom of the field meets other teams, and a spectator finds
// their team's games. playerId -> team; empty for every other format.
function arenaTeamsByPlayer() {
    const byPlayer = new Map();
    if (getActiveSettings().format !== 'arena') return byPlayer;
    getTeams().forEach(t => (t.squad || []).forEach(id => {
        if (!byPlayer.has(id)) byPlayer.set(id, t);
    }));
    return byPlayer;
}

function pairingRowHtml(p, idx, boardLabel, isPreview, teamOf = new Map()) {
    const readOnly = state.isSpectatorMode || isPreview;
    const white = state.players.find(pl => pl.id === p.white)
        || ((p.white === 'BYE' || p.white === 'WO') ? { lastName: p.white, givenName: '', id: p.white } : undefined);
    const black = (p.black === 'BYE' || p.black === 'WO')
        ? { lastName: p.black, givenName: '', id: p.black }
        : state.players.find(pl => pl.id === p.black);
    const clickable = (pl) => pl && pl.id !== 'WO' && pl.id !== 'BYE';
    const teamTag = (pl) => {
        const team = pl && teamOf.get(pl.id);
        return team && team.name ? ` <span style="color: var(--text-muted); font-size: 0.75rem; font-weight: 400;">${escapeHtml(team.name)}</span>` : '';
    };
    const teamMates = !!(white && black && teamOf.get(white.id) && teamOf.get(white.id) === teamOf.get(black.id));

    return `
        <tr data-white="${escapeHtml(p.white || '')}" data-black="${escapeHtml(p.black || '')}">
            <td style="vertical-align: middle; text-align: center; color: var(--text-muted); font-size: 0.8rem;">${boardLabel}${teamMates ? ` <i class="ri-group-line" title="${escapeHtml(tr('Team-mates'))}"></i>` : ''}</td>
            <td style="vertical-align: middle; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                <strong class="${clickable(white) ? 'clickable-player' : ''}" data-id="${escapeHtml(white ? white.id : '')}" style="${clickable(white) ? 'cursor: pointer; color: var(--primary);' : ''}">${formatPlayerName(white, true)}</strong>${teamTag(white)}
            </td>
            <td style="text-align: center; vertical-align: middle;">
                <div style="display: flex; justify-content: center; align-items: center; min-height: 32px;">
                    ${resultCellHtml(p, idx, readOnly)}
                </div>
            </td>
            <td style="vertical-align: middle; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                <strong class="${clickable(black) ? 'clickable-player' : ''}" data-id="${escapeHtml(black ? black.id : '')}" style="${clickable(black) ? 'cursor: pointer; color: var(--primary);' : ''}">${formatPlayerName(black, true)}</strong>${teamTag(black)}
            </td>
        </tr>`;
}

/**
 * The running score of one team match, in game points, as "2½ – 1½".
 *
 * Shown while the match is still going, so it is deliberately a total of what has been
 * decided rather than a verdict: match points are only awarded once every board is in
 * (see TeamEngine.calculateTeamStats), and showing "2 - 0" as though a 4-board match
 * were over would be worse than showing nothing.
 */
function matchScoreHtml(match, boards, settings) {
    const half = (n) => {
        const whole = Math.floor(n);
        if (n - whole === 0.5) return (whole === 0 ? '' : whole) + '½';
        return String(n);
    };
    let w = 0, b = 0, played = 0;
    boards.forEach(p => {
        if (!p.result) return;
        played++;
        const pts = window.TeamEngine.boardPoints(p.result, settings.pointSystem);
        if (p.whiteTeam === match.whiteTeam) { w += pts.white; b += pts.black; }
        else { w += pts.black; b += pts.white; }
    });
    if (played === 0) return '';
    const done = played === boards.length;
    return `<span style="font-weight: 700; ${done ? '' : 'opacity: 0.7;'}">${half(w)} – ${half(b)}</span>`
        + (done ? '' : ` <span style="color: var(--text-muted); font-size: 0.75rem;">${tr('so far')}</span>`);
}

function teamMatchRowsHtml(round, isPreview) {
    const teams = getTeams();
    const settings = getActiveSettings();
    const nameOf = (id) => {
        const t = teams.find(x => x.id === id);
        return t ? `${t.teamNo}. ${t.name}` : tr('Unknown team');
    };

    // Index once: a board's position in round.pairings is what the result handler needs,
    // and looking it up per row would be quadratic on a large event.
    const indexOf = new Map(round.pairings.map((p, idx) => [p, idx]));

    return round.matches.map(m => {
        const boards = round.pairings.filter(p => p.matchId === m.id);

        if (m.blackTeam === 'BYE') {
            return `
                <tr class="team-match-header">
                    <td colspan="4" style="background: var(--surface-tint); padding: 0.5rem 0.75rem; border-top: 2px solid var(--border-color);">
                        <strong>${escapeHtml(nameOf(m.whiteTeam))}</strong>
                        <span style="color: var(--text-muted); margin-left: 0.5rem;">— ${tr('bye this round')}</span>
                    </td>
                </tr>`;
        }

        const header = `
            <tr class="team-match-header">
                <td colspan="4" style="background: var(--surface-tint); padding: 0.5rem 0.75rem; border-top: 2px solid var(--border-color);">
                    <div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
                        <strong>${escapeHtml(nameOf(m.whiteTeam))}</strong>
                        <span style="color: var(--text-muted);">${tr('vs')}</span>
                        <strong>${escapeHtml(nameOf(m.blackTeam))}</strong>
                        <span style="margin-left: auto;">${matchScoreHtml(m, boards, settings)}</span>
                    </div>
                </td>
            </tr>`;

        // The board number shown is the board WITHIN the match, because that is what the
        // players and the arbiter call it — "board 2" means board 2 of this match, not
        // the sixth game in the hall.
        const rows = boards
            .map(p => pairingRowHtml(p, indexOf.get(p), p.board, isPreview))
            .join('');

        return header + rows;
    }).join('');
}

function renderPairingRows(round, isPreview) {
    if (Array.isArray(round.matches) && round.matches.length > 0) {
        return teamMatchRowsHtml(round, isPreview);
    }
    const teamOf = arenaTeamsByPlayer();
    return round.pairings.map((p, idx) => pairingRowHtml(p, idx, idx + 1, isPreview, teamOf)).join('');
}

// The pairings tab — and, when it is open, the full screen view, which is a second
// window onto the same round. Every path that changes which round is on screen goes
// through here (a round paired on this device, from a phone, or arriving from a
// co-arbiter), so refreshing the overlay here is what keeps a projector from being
// left on the previous round.
export function renderCurrentRound() {
    renderPairingsTab();
    const overlay = document.getElementById('pairings-fullscreen-overlay');
    if (overlay && overlay.classList.contains('active')) renderFullscreenPairings();
}

function renderPairingsTab() {
    if (!pairingsContainer) return;

    // Hidden up front so the early returns below are covered too: undoing the last
    // round must not leave a Publish button behind with nothing to publish.
    // syncPublishButton() turns it back on when there is a real round for it.
    if (btnPublishPairings) btnPublishPairings.style.display = 'none';

    // Fetch the pairing engine now rather than when the arbiter clicks Generate. It is a
    // ~550 KB download that only Swiss tournaments need, and doing it here means it overlaps
    // with them reading the round instead of adding to their wait. Idempotent and silent on
    // failure — generateNextRound() will surface any real problem.
    // A team tournament pairs its teams with the same engine, so it wants the same
    // head start on the download.
    // Only for an arbiter's own open tournament: a spectator never pairs, and this
    // render also runs at startup for every visitor, tournament or not.
    const fmt = getActiveSettings().format;
    if ((fmt === 'swiss' || fmt === 'team') && state.activeTournament && !state.isSpectatorMode) preloadPairingEngine();

    // The schedule editor lives in this page's header, and depends on what is open and who
    // is looking.
    syncScheduleButtons();

    const totalViewable = getViewableRoundCount();
    if (state.viewedRoundIndex >= totalViewable && totalViewable > 0) {
        state.viewedRoundIndex = totalViewable - 1;
    }

    // Disable nav buttons if needed
    if (btnPrevRound) btnPrevRound.disabled = state.viewedRoundIndex <= 0;
    if (btnNextRound) btnNextRound.disabled = state.viewedRoundIndex >= totalViewable - 1;

    if (totalViewable === 0) {
        currentRoundTitle.textContent = tr('No rounds generated yet.');
        if (btnPrevRound) btnPrevRound.style.visibility = 'hidden';
        if (btnNextRound) btnNextRound.style.visibility = 'hidden';

        pairingsContainer.innerHTML = `
            <div class="empty-state-container" style="min-height: 40vh;">
                <div class="empty-state-content glass" style="padding: 2rem;">
                    <i class="ri-sword-line" style="font-size: 3rem; color: var(--primary); margin-bottom: 1rem;"></i>
                    <h3>${tr('Ready for Round 1?')}</h3>
                    <p>${tr('Ensure all players are registered and present before generating pairings.')}</p>
                </div>
            </div>
        `;
        return;
    }

    if (btnPrevRound) btnPrevRound.style.visibility = 'visible';
    if (btnNextRound) btnNextRound.style.visibility = 'visible';

    const roundIndex = state.viewedRoundIndex;
    const { round, isPreview } = getViewedRound(roundIndex);
    if (!round) return;
    const isLatest = roundIndex === state.rounds.length - 1;
    const isNextToGenerate = roundIndex === state.rounds.length;

    // The round's place in the tournament, then when it starts — the question players ask
    // most often, answered where they are already looking (js/ui/schedule.js).
    const when = roundWhenLabel(round.roundNo);
    currentRoundTitle.textContent = tr('Round {n} of {total}', { n: round.roundNo, total: getActiveSettings().roundsCount })
        + (when ? ' · ' + when : '')
        + (isPreview ? ' — ' + tr('Preview') : '');

    // Edit actions only apply to generated rounds
    if (btnEditPairings) btnEditPairings.style.display = (state.isSpectatorMode || isPreview) ? 'none' : 'flex';
    if (btnRegisterResults) btnRegisterResults.style.display = (state.isSpectatorMode || isPreview) ? 'none' : 'flex';
    syncPublishButton(round, isPreview);

    // Round management: undo only on the latest generated round; generate on the
    // latest generated round or while previewing the next round of the schedule.
    if (btnUndoRound) btnUndoRound.style.display = isLatest ? '' : 'none';
    if (btnGenerateRound) btnGenerateRound.style.display = (isLatest || isNextToGenerate) ? '' : 'none';

    const previewBanner = isPreview ? `
        <div style="display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 1rem; margin-bottom: 0.75rem; border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-muted); font-size: 0.85rem;">
            <i class="ri-calendar-schedule-line"></i>
            ${escapeHtml(tr('This round has not been played yet — pairings follow the round robin schedule. Results can be entered once the round is generated.'))}
        </div>` : '';

    // Said where the arbiter is already looking, because a round nobody can see is
    // not something to discover from a player's question.
    const heldBack = !state.isSpectatorMode && !isPreview && pairingsHeldBack()
        && state.viewedRoundIndex < state.rounds.length;
    const publishBanner = (heldBack && !roundIsPublished(round)) ? `
        <div style="display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 1rem; margin-bottom: 0.75rem; border: 1px solid var(--warning); border-radius: 8px; color: var(--warning); font-size: 0.85rem;">
            <i class="ri-eye-off-line"></i>
            ${tr('Not published yet — spectators cannot see this round. Press Publish Pairings when you are ready.')}
        </div>` : '';

    pairingsContainer.innerHTML = `
        ${previewBanner}
        ${publishBanner}
        <div class="card glass">
            <div class="card-body">
                <table class="data-table compact-table pairings-table">
                    <thead>
                        <tr>
                            <th style="width: 50px; text-align: center;">${tr('Board')}</th>
                            <th style="width: calc(50% - 100px);">${tr('White')}</th>
                            <th style="width: 150px; text-align: center;">${tr('Result')}</th>
                            <th style="width: calc(50% - 100px);">${tr('Black')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${renderPairingRows(round, isPreview)}
                    </tbody>
                </table>
            </div>
        </div>
    `;

    // Add event listeners for automatic saving (real rounds only — previews
    // render static result cells, so no selects exist there)
    pairingsContainer.querySelectorAll('.pairing-result-select').forEach(select => {
        select.addEventListener('change', () => {
            const idx = select.dataset.index;
            setResult(round.pairings[idx], select.value);
            saveToLocalStorage();
            renderStandings(); // Immediately reflect in standings
            // The Scan Results phones show the round too; without this they went on
            // showing the result the arbiter had just changed or removed.
            if (window.app && window.app.syncScanSessions) window.app.syncScanSessions();
        });
    });

    // Keep a search's highlight across re-renders (a result coming in), without
    // scrolling the page away from wherever the reader is.
    applyPairingsSearchHighlight(false);
}

// ---------------------------------------------------------------------------
// "Which board is my child on?" — the Pairings tab's player search. It highlights
// the board of every player whose name matches, like the Standings search, and
// scrolls to the first once the reader stops typing.
// ---------------------------------------------------------------------------

const pairingsSearch = document.getElementById('pairings-search');
let pairingsSearchTimer = null;

function nameMatches(player, q) {
    if (!player) return false;
    const given = (player.givenName || '').toLowerCase();
    const last = (player.lastName || '').toLowerCase();
    return `${given} ${last}`.includes(q) || `${last} ${given}`.includes(q) || `${last}, ${given}`.includes(q);
}

export function applyPairingsSearchHighlight(scroll = false) {
    if (!pairingsContainer) return;
    const q = pairingsSearch ? pairingsSearch.value.trim().toLowerCase() : '';
    let first = null;
    pairingsContainer.querySelectorAll('tr[data-white]').forEach(row => {
        const hit = !!q && [row.dataset.white, row.dataset.black]
            .some(id => nameMatches(state.players.find(p => String(p.id) === String(id)), q));
        row.classList.toggle('row-highlight', hit);
        if (hit && !first) first = row;
    });
    if (scroll && first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

if (pairingsSearch) {
    pairingsSearch.addEventListener('input', () => {
        applyPairingsSearchHighlight(false);
        clearTimeout(pairingsSearchTimer);
        pairingsSearchTimer = setTimeout(() => applyPairingsSearchHighlight(true), 350);
    });
}

// ---------------------------------------------------------------------------
// Full Screen Pairings View
// A distraction-free overlay showing only the current round's pairings,
// balanced across 1–3 columns (≤20 → 1 col, 21–40 → 2 cols, 41–60 → 3 cols).
// Results stay editable inline; nothing else is reachable until exit.
// ---------------------------------------------------------------------------

const RESULT_LABELS = {
    '1-0': '1 - 0',
    '0.5-0.5': '½ - ½',
    '0-1': '0 - 1',
    '+/-': '+ / -',
    '-/+': '- / +',
    '-/-': '- / -'
};

// Split n boards into `cols` balanced, top-heavy chunks (e.g. 21 → 11 + 10).
function balanceColumns(count) {
    const cols = count <= 20 ? 1 : count <= 40 ? 2 : 3;
    const perCol = Math.ceil(count / cols);
    const chunks = [];
    for (let start = 0; start < count; start += perCol) {
        chunks.push([start, Math.min(start + perCol, count)]);
    }
    return chunks;
}

export function renderFullscreenPairings() {
    const body = document.getElementById('pairings-fullscreen-body');
    const title = document.getElementById('pairings-fullscreen-title');
    if (!body) return;

    const { round, isPreview } = getViewedRound(state.viewedRoundIndex);
    if (!round) {
        body.innerHTML = '';
        return;
    }

    if (title) title.textContent = tr('Round {n} of {total} — Pairings', { n: round.roundNo, total: getActiveSettings().roundsCount }) + (isPreview ? ' (' + tr('Preview') + ')' : '');

    const readOnly = state.isSpectatorMode || isPreview;

    const rowHtml = (idx) => {
        const p = round.pairings[idx];
        const white = state.players.find(pl => pl.id === p.white);
        const black = (p.black === 'BYE' || p.black === 'WO')
            ? { lastName: p.black, givenName: '', id: p.black }
            : state.players.find(pl => pl.id === p.black);

        let resultCell;
        if (readOnly) {
            const formatted = RESULT_LABELS[p.result] || p.result || '—';
            resultCell = `<span class="fs-result-static">${formatted}</span>`;
        } else {
            resultCell = `<select class="fs-result-select" data-index="${idx}">
                <option value="" ${!p.result ? 'selected' : ''}>---</option>
                <option value="1-0" ${p.result === '1-0' ? 'selected' : ''}>1 - 0</option>
                <option value="0.5-0.5" ${p.result === '0.5-0.5' ? 'selected' : ''}>½ - ½</option>
                <option value="0-1" ${p.result === '0-1' ? 'selected' : ''}>0 - 1</option>
                <option value="+/-" ${p.result === '+/-' ? 'selected' : ''}>+ / -</option>
                <option value="-/+" ${p.result === '-/+' ? 'selected' : ''}>- / +</option>
                <option value="-/-" ${p.result === '-/-' ? 'selected' : ''}>- / -</option>
            </select>`;
        }

        // In a team round the board number is the board WITHIN the match. Numbering
        // straight down the hall would put "board 6" on a four-board match, which is not
        // what anyone in the room calls it.
        const boardLabel = p.board || (idx + 1);

        return `
            <div class="fs-pairing-row">
                <div class="fs-board-no">${boardLabel}</div>
                <div class="fs-white">${formatPlayerName(white, true)}</div>
                <div class="fs-result">${resultCell}</div>
                <div class="fs-black">${formatPlayerName(black, true)}</div>
            </div>
        `;
    };

    // A team match's own heading, spanning the four columns of the grid. This is the
    // projector view, so the team names are the thing people in the room look for —
    // without them a team round is just an unexplained list of names.
    const matchHeaderHtml = (match) => {
        const teams = getTeams();
        const nameOf = (id) => {
            const t = teams.find(x => x.id === id);
            return t ? `${t.teamNo}. ${t.name}` : tr('Unknown team');
        };
        const boards = round.pairings.filter(x => x.matchId === match.id);
        const score = match.blackTeam === 'BYE'
            ? `<span class="fs-match-score">${tr('bye')}</span>`
            : `<span class="fs-match-score">${matchScoreHtml(match, boards, getActiveSettings())}</span>`;
        const versus = match.blackTeam === 'BYE'
            ? escapeHtml(nameOf(match.whiteTeam))
            : `${escapeHtml(nameOf(match.whiteTeam))} <span class="fs-match-vs">${tr('vs')}</span> ${escapeHtml(nameOf(match.blackTeam))}`;
        return `<div class="fs-match-header">${versus}${score}</div>`;
    };

    const headerHtml = `
        <div class="fs-pairings-header">
            <div class="fs-board-no">${tr('Board')}</div>
            <div class="fs-white">${tr('White')}</div>
            <div class="fs-result">${tr('Result')}</div>
            <div class="fs-black">${tr('Black')}</div>
        </div>
    `;

    // A team round is split into columns by MATCH, never mid-match: a match broken
    // across two columns is unreadable from the back of the hall.
    const isTeamRound = Array.isArray(round.matches) && round.matches.length > 0;

    if (isTeamRound) {
        const indexOf = new Map(round.pairings.map((p, i) => [p, i]));
        const blocks = round.matches.map(m => {
            const boards = round.pairings.filter(p => p.matchId === m.id);
            return matchHeaderHtml(m) + boards.map(p => rowHtml(indexOf.get(p))).join('');
        });
        // Balance on the number of ROWS each block costs, so columns come out roughly
        // the same height whether the matches are four boards or eight.
        const weights = round.matches.map(m =>
            1 + round.pairings.filter(p => p.matchId === m.id).length);
        const total = weights.reduce((a, b) => a + b, 0);
        const cols = total <= 20 ? 1 : total <= 40 ? 2 : 3;
        const perCol = Math.ceil(total / cols);

        const columns = [];
        let current = [];
        let used = 0;
        blocks.forEach((block, i) => {
            if (used > 0 && used + weights[i] > perCol && columns.length < cols - 1) {
                columns.push(current); current = []; used = 0;
            }
            current.push(block);
            used += weights[i];
        });
        if (current.length) columns.push(current);

        // fs-team-mode drops the zebra striping: the rows are broken up by match headers,
        // and :nth-of-type counts those too, so the stripes would land arbitrarily. The
        // match headings separate the blocks instead, which reads better anyway.
        body.innerHTML = columns
            .map(col => `<div class="fs-pairings-column fs-team-mode">${headerHtml}${col.join('')}</div>`)
            .join('');
    } else {
        const chunks = balanceColumns(round.pairings.length);
        body.innerHTML = chunks.map(([start, end]) => {
            let rows = '';
            for (let i = start; i < end; i++) rows += rowHtml(i);
            return `<div class="fs-pairings-column">${headerHtml}${rows}</div>`;
        }).join('');
    }

    if (!readOnly) {
        body.querySelectorAll('.fs-result-select').forEach(select => {
            select.addEventListener('change', () => {
                const idx = parseInt(select.dataset.index, 10);
                setResult(round.pairings[idx], select.value);
                saveToLocalStorage();
                renderStandings();
                if (window.app && window.app.syncScanSessions) window.app.syncScanSessions();
            });
        });
    }

    sizeFullscreenPairingsText();
}

// A column's rows share the column's height via flex, so fewer boards means
// taller rows — but the CSS clamp() alone caps the font size well below what
// tall rows have room for. Measure the actual per-row height once laid out
// and scale the font to fill it, so few-board rounds get proportionally
// bigger, more legible text instead of being stuck at the clamp's ceiling.
// A second pass then shrinks that size further if it would clip the longest
// player name — the name columns must never truncate a name.
function sizeFullscreenPairingsText() {
    const body = document.getElementById('pairings-fullscreen-body');
    if (!body) return;
    const columns = body.querySelectorAll('.fs-pairings-column');
    if (columns.length === 0) return;

    const firstCol = columns[0];
    const header = firstCol.querySelector('.fs-pairings-header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const maxRows = Math.max(...Array.from(columns).map(c => c.querySelectorAll('.fs-pairing-row').length), 1);
    const availableHeight = firstCol.clientHeight - headerHeight;
    const rowHeight = availableHeight / maxRows;

    let fontSize = Math.max(12, Math.min(rowHeight * 0.4, 42));
    body.style.setProperty('--fs-row-font', fontSize + 'px');

    // Names are nowrap + overflow:hidden, so scrollWidth is the name's full
    // unclipped width regardless of the visible (clientWidth) box — comparing
    // the two tells us exactly how much a name would be cut off at this size.
    let worstRatio = 1;
    body.querySelectorAll('.fs-white, .fs-black').forEach(el => {
        const cw = el.clientWidth;
        if (cw <= 0) return;
        worstRatio = Math.max(worstRatio, el.scrollWidth / cw);
    });
    if (worstRatio > 1) {
        fontSize = Math.max(10, fontSize / worstRatio);
        body.style.setProperty('--fs-row-font', fontSize + 'px');
    }
}

window.addEventListener('resize', () => {
    const overlay = document.getElementById('pairings-fullscreen-overlay');
    if (overlay && overlay.classList.contains('active')) sizeFullscreenPairingsText();
});

export function openFullscreenPairings() {
    if (!state.rounds || state.rounds.length === 0) {
        window.Dialog.warn(tr('Generate a round first before opening the full screen view.'), tr('No Pairings'));
        return;
    }

    const overlay = document.getElementById('pairings-fullscreen-overlay');
    if (!overlay) return;

    renderFullscreenPairings();
    overlay.classList.add('active');

    // Try to enter native browser full screen; harmless if the browser refuses.
    if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
    }
}

export function closeFullscreenPairings() {
    const overlay = document.getElementById('pairings-fullscreen-overlay');
    if (!overlay || !overlay.classList.contains('active')) return;

    overlay.classList.remove('active');

    if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
    }

    // Reflect any results registered while in full screen back into the tab.
    renderCurrentRound();
    renderStandings();
}

// When native full screen is exited (Esc, F11, browser UI), tear down our
// overlay too so a single Esc leaves both at once. Browsers reserve the Esc
// keypress for exiting native full screen and usually don't dispatch a keydown
// for it, so this event — not a keydown listener — is the reliable signal.
document.addEventListener('fullscreenchange', () => {
    if (document.fullscreenElement) return; // just entered — ignore
    const overlay = document.getElementById('pairings-fullscreen-overlay');
    if (overlay && overlay.classList.contains('active')) {
        closeFullscreenPairings();
    }
});

// Fallback for when the browser declined native full screen (overlay only):
// close it on Esc directly.
window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || document.fullscreenElement) return;
    const overlay = document.getElementById('pairings-fullscreen-overlay');
    if (overlay && overlay.classList.contains('active')) {
        closeFullscreenPairings();
    }
});

/**
 * Pair one round of a TEAM tournament.
 *
 * The teams are paired by bbpPairings and coloured under C.04.6 art. 4.3 — see
 * js/services/team-pairing-engine.js. What comes back is a list of team matches plus
 * the individual board games they expand into, and BOTH are stored on the round:
 * `matches` is what the team standings and the TRF team records read, `pairings` is
 * the ordinary flat list every existing screen already knows how to render and take
 * results for.
 */
async function generateNextTeamRound(settings) {
    const teams = getTeams();
    const entered = teams.filter(t => t.status !== false);
    if (entered.length < 2) {
        await window.Dialog.warn(
            tr('At least two teams must be entered before a round can be paired.'),
            tr('Not Enough Teams')
        );
        return;
    }

    const ts = settings.teamSettings || {};
    const short = entered.filter(t => (t.squad || []).length < (ts.boards || 4));
    if (short.length > 0) {
        const names = short.map(t => t.name).join(', ');
        const proceed = await window.Dialog.confirm(
            tr('These teams have fewer players registered than the match has boards: {names}.', { names }) +
            '\n\n' +
            tr('Their empty boards will be forfeited. Generate the round anyway?'),
            tr('Short-Handed Teams'),
            tr('Generate Anyway'),
            false,
            tr('Cancel'),
            'warning'
        );
        if (!proceed) return;
    }

    // Line-up legality is checked HERE, not while a captain is typing one. A captain
    // filling four boards passes through invalid states on the way (two players on one
    // board mid-swap), so the picker stores whatever they choose and this is the point
    // where it has to hold — the board order they field is about to become the pairings.
    const roundNo = state.rounds.length + 1;
    const illegal = [];
    entered.forEach(t => {
        const chosen = (t.lineups || {})[roundNo];
        if (!Array.isArray(chosen) || !chosen.some(id => id)) return; // falls back to squad order
        const check = window.TeamEngine.validateLineup(
            t.squad || [], chosen, ts.boards || 4,
            { reservesBottomOnly: !!ts.reservesBottomOnly }
        );
        if (!check.ok) illegal.push(`• ${t.name}: ${check.errors.map(e => tr(e)).join(' ')}`);
    });
    if (illegal.length > 0) {
        const proceed = await window.Dialog.confirm(
            tr('Some line-ups break the registered board order:') +
            '\n\n' + illegal.join('\n') + '\n\n' +
            tr('Pair the round with them anyway?'),
            tr('Illegal Line-up'), tr('Pair Anyway'), false, tr('Cancel'), 'warning'
        );
        if (!proceed) return;
    }

    const btn = document.getElementById('btn-generate-round');
    if (btn) btn.innerHTML = `<i class="ri-loader-4-line spin"></i> ${tr('Generating pairings...')}`;

    let result;
    try {
        result = await generateTeamRound({
            teams: entered,
            rounds: state.rounds,
            settings: { ...ts, roundsCount: settings.roundsCount },
            // A captain's chosen line-up for the round about to be paired. Teams without
            // one field the top of their squad; see TeamEngine.defaultLineup.
            lineups: Object.fromEntries(
                entered.map(t => [t.id, (t.lineups || {})[roundNo] || null])
            ),
            isAvailable: (playerId) => {
                const p = state.players.find(pl => pl.id === playerId);
                return !p || p.status !== false;
            },
            name: state.activeTournament.name
        });
    } catch (e) {
        console.error('Team pairing failed:', e);
        if (btn) btn.innerHTML = `<i class="ri-play-line"></i> ${tr('Generate Round')}`;
        await window.Dialog.error(e.message || tr('Could not generate pairings.'), tr('Pairing Failed'));
        return;
    }

    if (btn) btn.innerHTML = `<i class="ri-play-line"></i> ${tr('Generate Round')}`;
    (result.warnings || []).forEach(w => console.warn('Team pairing warning:', w));

    // The single place where pairing teams through an individual-Swiss engine can
    // legitimately depart from C.04.6, surfaced rather than swallowed. The engine
    // enforces the Dutch absolute colour rules and the team rules exclude them, so a
    // team sitting at that boundary may have been given an opponent the team rules
    // would not have chosen.
    if ((result.colourRisks || []).length > 0) {
        const nameOf = (id) => (teams.find(t => t.id === id) || {}).name || tr('Unknown');
        const list = result.colourRisks.map(r => `• ${nameOf(r.teamId)} — ${r.reason}`).join('\n');
        const proceed = await window.Dialog.confirm(
            tr('Some teams are at the colour limits the individual Swiss rules enforce but the team rules do not:') +
            '\n' + list + '\n\n' +
            tr('Their pairing may differ from a strict C.04.6 pairing. Accept this round?'),
            tr('Check Colours'),
            tr('Accept Round'),
            false,
            tr('Cancel'),
            'warning'
        );
        if (!proceed) return;
    }

    const newRound = {
        roundNo: state.rounds.length + 1,
        matches: result.matches,
        pairings: result.pairings
    };
    // 'auto' writes no flag at all, so a round looks exactly as it always did.
    if (pairingsHeldBack()) newRound.published = false;

    state.rounds.push(newRound);
    state.viewedRoundIndex = state.rounds.length - 1;
    bumpStructureRev();

    const closedName = state.rounds.length === 1 ? closeRegistrationOnFirstPairing() : '';

    saveToLocalStorage();
    updateUIState();
    renderCurrentRound();
    renderStandings();
    if (window.app && window.app.syncScanSessions) window.app.syncScanSessions();

    if (closedName) {
        await window.Dialog.warn(
            tr('Online registration for {name} is now closed, because the first round has been paired. You can reopen it under Options if you still want entries.', { name: closedName }),
            tr('Registration Closed')
        );
    }
}

export async function generateNextRound() {
    const settings = getActiveSettings();
    if (state.rounds.length >= parseInt(settings.roundsCount)) {
        await window.Dialog.warn(tr('Maximum number of rounds ({n}) reached.', { n: settings.roundsCount }), tr('Max Rounds'));
        return;
    }

    // Check if current round results are saved
    if (state.rounds.length > 0) {
        const currentRound = state.rounds[state.rounds.length - 1];
        const incomplete = currentRound.pairings.some(p => !p.result && p.black !== 'BYE' && p.black !== 'WO');
        if (incomplete) {
            await window.Dialog.warn(tr('Please enter and save all results for the current round before generating the next one.'), tr('Missing Results'));
            return;
        }
    }

    // A team tournament pairs TEAMS, not players, so it takes its own path before any
    // of the player-level checks below (starting numbers, present count) — none of
    // which describe the field being paired.
    if (settings.format === 'team') {
        await generateNextTeamRound(settings);
        return;
    }

    const activePlayers = state.players.filter(p => p.status);
    if (activePlayers.length < 2) {
        await window.Dialog.warn(tr('Need at least 2 present players to generate pairings.'), tr('Not Enough Players'));
        return;
    }

    // Requested byes (js/core/bye-requests.js). A player who asked to sit this round out is
    // not in the field being paired and gets a bye row of their own once the rest is paired.
    //
    // The engines are told in different ways, because they take the field differently:
    // the Swiss engine is fed a file, and PairingTRF.build reads the requests itself while
    // writing it (that file IS how a player is excluded); Monrad and the Arena take the
    // whole roster plus the ids to leave out. Never a roster with those players removed:
    // their earlier games would then not be scored at all, and the players they met would
    // be paired as though those games had never been played.
    const roundNo = state.rounds.length + 1;
    const requestedByes = window.ByeRequests.honoursFormat(settings.format)
        ? window.ByeRequests.pairingsFor(state.players, roundNo)
        : [];
    const sittingOut = new Set(requestedByes.map(p => p.white));
    if (sittingOut.size > 0 && activePlayers.filter(p => !sittingOut.has(p.id)).length < 2) {
        await window.Dialog.warn(
            tr('Too many players have asked to sit round {n} out — fewer than two are left to pair. Change a requested bye under Players first.', { n: roundNo }),
            tr('Not Enough Players')
        );
        return;
    }

    // An Arena keeps team-mates apart, so pairing one without teams, or with present players
    // nobody has placed, is worth a word first. Asked when there are no teams at all, and
    // before round 1 for players left out of them: a late entry may well be meant to play
    // on their own, and a question every round would only get clicked away.
    if (settings.format === 'arena') {
        const teams = getTeams();
        const inTeam = new Set(teams.flatMap(t => t.squad || []));
        const loose = activePlayers.filter(p => !sittingOut.has(p.id) && !inTeam.has(p.id));
        if (teams.length === 0 || (state.rounds.length === 0 && loose.length > 0)) {
            const proceed = await window.Dialog.confirm(
                teams.length === 0
                    ? tr('This Arena has no teams yet, so nobody is kept apart. Make the teams on the Players tab first, or pair the round anyway.')
                    : tr('{n} present players are not in a team, and can be paired with anyone. Pair the round anyway?', { n: loose.length }),
                tr('Teams'),
                tr('Pair Anyway'),
                false,
                tr('Cancel'),
                'warning'
            );
            if (!proceed) return;
        }
    }

    // Check for starting numbers
    const missingSNR = activePlayers.some(p => !p.startNo);
    if (missingSNR) {
        const result = await assignStartingNumbers(false);
        if (result === 'cancelled' || result === null) {
            return;
        }
    }

    // NSF §4: start number 1 plays black in round 1, so Monrad (and Konrad) default to a
    // 'black' seed.
    let seedColor = settings.seedColor || defaultSeedColor(settings.format);
    if (state.rounds.length === 0 && seedColor === 'random') {
        seedColor = Math.random() < 0.5 ? 'white' : 'black';
    }

    const btn = document.getElementById('btn-generate-round');

    let pairings;
    let konradOverCap = [];
    let arenaRound = null;
    if (settings.format === 'swiss') {
        if (btn) btn.innerHTML = `<i class="ri-loader-4-line spin"></i> ${tr('Generating pairings...')}`;
        try {
            // Pairings come from bbpPairings (WebAssembly) via pairing-engine.js.
            // `seedColor` is passed explicitly because a 'random' setting has already been
            // resolved to a concrete colour above, and the engine requires a real one.
            const result = await generateSwissPairings(state, { seedColor });
            pairings = result.pairings;
            // Non-fatal data oddities — e.g. a double forfeit, which the standings count as
            // half a point each but the pairing rules count as zero.
            result.warnings.forEach(w => console.warn('Pairing warning:', w));
        } catch (e) {
            // Every failure path now carries a specific reason (missing starting numbers, no
            // legal pairing, malformed data), instead of one generic message for all of them.
            console.error('Swiss pairing failed:', e);
            if (btn) btn.innerHTML = `<i class="ri-play-line"></i> ${tr('Generate Round')}`;
            await window.Dialog.error(e.message || tr('Could not generate pairings.'), tr('Pairing Failed'));
            return;
        }
    } else if (settings.format === 'monrad') {
        if (btn) btn.innerHTML = `<i class="ri-loader-4-line spin"></i> ${tr('Generating pairings...')}`;
        const totalRounds = parseInt(settings.roundsCount || settings.rounds) || 0;
        // The whole roster, and the players sitting this round out on a request named
        // separately; see above.
        pairings = MonradEngine.generatePairings(state.players, state.rounds, settings.pointSystem, seedColor, totalRounds, { exclude: sittingOut });
    } else if (settings.format === 'konrad') {
        // Only the players marked present are paired; the absent ones get the bye the
        // settings give them (js/core/konrad-engine.js).
        if (btn) btn.innerHTML = `<i class="ri-loader-4-line spin"></i> ${tr('Generating pairings...')}`;
        const result = window.KonradEngine.generateRound(state.players, state.rounds, {
            pointSystem: settings.pointSystem,
            seedColor,
            totalRounds: parseInt(settings.roundsCount || settings.rounds) || 0,
            settings: settings.konradSettings
        });
        pairings = result.pairings;
        konradOverCap = result.overCap;
    } else if (settings.format === 'arena') {
        // Paired from the bottom up, against other teams where possible
        // (js/core/arena-engine.js). The players sitting this round out on a request are
        // left out of the field but still in the roster, so their games keep counting.
        if (btn) btn.innerHTML = `<i class="ri-loader-4-line spin"></i> ${tr('Generating pairings...')}`;
        // A large field takes a moment: let the button show it before the work starts.
        await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
        try {
            arenaRound = window.ArenaEngine.generateRound(state.players, state.rounds, {
                teams: getTeams(),
                pointSystem: settings.pointSystem,
                seedColor,
                sittingOut: [...sittingOut]
            });
            pairings = arenaRound.pairings;
        } catch (e) {
            console.error('Arena pairing failed:', e);
            if (btn) btn.innerHTML = `<i class="ri-play-line"></i> ${tr('Generate Round')}`;
            await window.Dialog.error(tr(e.message || 'Could not generate pairings.'), tr('Pairing Failed'));
            return;
        }
    } else {
        if (btn) btn.innerHTML = `<i class="ri-loader-4-line spin"></i> ${tr('Generating pairings...')}`;
        const roundNo = state.rounds.length + 1;

        // Round robin uses a FIXED Berger schedule over the whole field, NOT filtered
        // by present status (absent players are handled as forfeits when results are
        // entered). The pairing-number order is PINNED on the tournament/group when
        // round 1 is generated (`rrPairingOrder`) and reused for every later round —
        // otherwise reassigning start numbers mid-tournament would silently shift the
        // whole table and cause rematches.
        const roster = state.players.filter(p => p.startNo);
        const bySnr = (a, b) => (a.startNo || 999) - (b.startNo || 999);

        // Per-group settings live on the active group when the tournament has groups.
        const store = (() => {
            const t = state.activeTournament;
            if (t.groups && state.activeGroupId) {
                const g = t.groups.find(gr => String(gr.id) === String(state.activeGroupId));
                if (g) return g;
            }
            return t;
        })();

        let orderIds = Array.isArray(store.rrPairingOrder)
            ? store.rrPairingOrder.filter(id => roster.some(p => p.id === id))
            : [];
        if (roundNo === 1) {
            // (Re)pin at round 1: current start-number order becomes the schedule.
            orderIds = [...roster].sort(bySnr).map(p => p.id);
        } else {
            // Keep existing pairing numbers stable; late additions (or legacy
            // tournaments without a stored order) are appended in start-number order.
            const extras = roster.filter(p => !orderIds.includes(p.id)).sort(bySnr);
            orderIds = [...orderIds, ...extras.map(p => p.id)];
        }
        store.rrPairingOrder = orderIds; // persisted by saveToLocalStorage on commit

        const field = orderIds.map(id => state.players.find(p => p.id === id));
        const fieldSize = field.length;
        const maxRounds = fieldSize % 2 === 0 ? fieldSize - 1 : fieldSize;
        if (roundNo > maxRounds) {
            if (btn) btn.innerHTML = `<i class="ri-play-line"></i> ${tr('Generate Round')}`;
            await window.Dialog.success(
                tr('A round robin with {n} players runs for at most {r} {rounds} — everyone has already played everyone.', { n: fieldSize, r: maxRounds, rounds: maxRounds === 1 ? tr('round') : tr('rounds') }),
                tr('Round Robin Complete')
            );
            return;
        }

        // generateValidRound follows the Berger table, but verifies it against the
        // games actually played and falls back to a min-rematch pairing when history
        // deviates from the schedule (older engine, renumbered players, edited rounds).
        const rrResult = RoundRobinEngine.generateValidRound(field, state.rounds, roundNo);
        pairings = rrResult.pairings;

        if (rrResult.deviated) {
            if (btn) btn.innerHTML = `<i class="ri-play-line"></i> ${tr('Generate Round')}`;
            if (rrResult.repeats.length > 0) {
                const nameOf = (id) => {
                    const p = state.players.find(pl => pl.id === id);
                    return p ? formatPlayerName(p) : tr('Unknown');
                };
                const list = rrResult.repeats.map(([w, b]) => `• ${nameOf(w)} – ${nameOf(b)}`).join('\n');
                const proceed = await window.Dialog.confirm(
                    tr('The played rounds no longer match the round robin schedule, and it is not possible to pair this round without a rematch.') + '\n\n' +
                    tr('{count} pairing(s) repeat an earlier game:', { count: rrResult.repeats.length }) + '\n' + list + '\n\n' +
                    tr('Do you want to generate the round anyway?'),
                    tr('Unavoidable Rematch'),
                    tr('Generate Anyway'),
                    false,
                    tr('Cancel'),
                    'warning'
                );
                if (!proceed) return;
            } else {
                await window.Dialog.success(
                    tr('The games played so far deviate from the Berger schedule (this can happen if start numbers were changed after round 1). A round avoiding all rematches was generated instead.'),
                    tr('Schedule Adjusted')
                );
            }
        }
    }

    if (btn) btn.innerHTML = `<i class="ri-play-line"></i> ${tr('Generate Round')}`;

    if (!pairings || pairings.length === 0) {
        return;
    }

    // Monrad: the engine first attempts a pairing with zero rematches and only falls back to
    // repeats when none is possible. So any repeat in the output means a rematch is unavoidable —
    // require explicit arbiter confirmation (override) before committing the round.
    if (settings.format === 'monrad') {
        const priorOpponents = {};
        const isReal = (id) => id && id !== 'WO' && id !== 'BYE';
        state.rounds.forEach(r => r.pairings.forEach(pr => {
            if (isReal(pr.white) && isReal(pr.black)) {
                (priorOpponents[pr.white] = priorOpponents[pr.white] || new Set()).add(pr.black);
                (priorOpponents[pr.black] = priorOpponents[pr.black] || new Set()).add(pr.white);
            }
        }));

        const repeatPairs = pairings.filter(pr =>
            isReal(pr.white) && isReal(pr.black) &&
            priorOpponents[pr.white] && priorOpponents[pr.white].has(pr.black)
        );

        if (repeatPairs.length > 0) {
            const nameOf = (id) => {
                const p = state.players.find(pl => pl.id === id);
                return p ? formatPlayerName(p) : tr('Unknown');
            };
            const list = repeatPairs.map(pr => `• ${nameOf(pr.white)} – ${nameOf(pr.black)}`).join('\n');
            const proceed = await window.Dialog.confirm(
                tr('It is not possible to pair this round without a rematch.') + '\n\n' +
                tr('{count} pairing(s) repeat an earlier game:', { count: repeatPairs.length }) + '\n' + list + '\n\n' +
                tr('Do you want to generate the round anyway?'),
                tr('Unavoidable Rematch'),
                tr('Generate Anyway'),
                false,
                tr('Cancel'),
                'warning'
            );
            if (!proceed) {
                return;
            }
        }
    }

    // Konrad: meeting again is part of the system, up to the allowance. A pair beyond it
    // only comes out of the engine when there was no other way to pair the round, and the
    // arbiter decides whether to accept that — same as a Monrad rematch.
    if (settings.format === 'konrad' && konradOverCap.length > 0) {
        const nameOf = (id) => {
            const p = state.players.find(pl => pl.id === id);
            return p ? formatPlayerName(p) : tr('Unknown');
        };
        const max = settings.konradSettings.maxMeetings;
        const list = konradOverCap.map(([w, b]) => `• ${nameOf(w)} – ${nameOf(b)}`).join('\n');
        const proceed = await window.Dialog.confirm(
            tr(max === 1
                ? 'It is not possible to pair this round without a rematch.'
                : 'It is not possible to pair this round without two players meeting more than {max} times.', { max }) + '\n\n' +
            tr('{count} pairing(s) go beyond the allowance:', { count: konradOverCap.length }) + '\n' + list + '\n\n' +
            tr('Do you want to generate the round anyway?'),
            tr('Unavoidable Rematch'),
            tr('Generate Anyway'),
            false,
            tr('Cancel'),
            'warning'
        );
        if (!proceed) return;
    }

    // Arena: a rematch, or a colour past the limit, only comes out of the engine when the
    // round could not be paired without one, and the arbiter decides whether to accept it,
    // as for a Monrad rematch. Going past the score limit needs no question: the rule
    // itself allows it when there is no other way.
    if (arenaRound && (arenaRound.rematches.length > 0 || arenaRound.colourBreaks.length > 0)) {
        const nameOf = (id) => {
            const p = state.players.find(pl => pl.id === id);
            return p ? formatPlayerName(p) : tr('Unknown');
        };
        const broken = new Map();
        const note = (list, why) => list.forEach(([w, b]) => {
            const key = `${w}|${b}`;
            if (!broken.has(key)) broken.set(key, { w, b, why: [] });
            broken.get(key).why.push(why);
        });
        note(arenaRound.rematches, tr('they have met before'));
        note(arenaRound.colourBreaks, tr('too many games with one colour'));
        const list = [...broken.values()]
            .map(({ w, b, why }) => `• ${nameOf(w)} – ${nameOf(b)} (${why.join(', ')})`)
            .join('\n');
        const proceed = await window.Dialog.confirm(
            tr('It is not possible to pair this round without breaking a rule.') + '\n\n' +
            tr('{count} pairing(s) break a rule:', { count: broken.size }) + '\n' + list + '\n\n' +
            tr('Do you want to generate the round anyway?'),
            tr('Rules Broken'),
            tr('Generate Anyway'),
            false,
            tr('Cancel'),
            'warning'
        );
        if (!proceed) return;
    }

    // The requested byes go in last, after every confirmation the arbiter might still have
    // cancelled the round at — a bye row for a round that was never generated would be a
    // half point awarded for nothing. The sort below then moves them to the bottom of the
    // list, where every other bye already sits.
    if (requestedByes.length) pairings = [...pairings, ...requestedByes];

    pairings.sort((a, b) => {
        const aIsWO = a.white === 'WO' || a.black === 'WO' || a.white === 'BYE' || a.black === 'BYE';
        const bIsWO = b.white === 'WO' || b.black === 'WO' || b.white === 'BYE' || b.black === 'BYE';
        if (aIsWO && !bIsWO) return 1;
        if (!aIsWO && bIsWO) return -1;
        return 0;
    });

    const newRound = {
        roundNo: state.rounds.length + 1,
        pairings: pairings
    };
    if (pairingsHeldBack()) newRound.published = false;

    state.rounds.push(newRound);
    state.viewedRoundIndex = state.rounds.length - 1;
    bumpStructureRev();

    // The field is now fixed, so stop taking entries into it (see
    // closeRegistrationOnFirstPairing in state.js for why the deadline doesn't
    // decide this). Only on the round that actually starts the event.
    const closedName = state.rounds.length === 1 ? closeRegistrationOnFirstPairing() : '';

    saveToLocalStorage();
    updateUIState();
    renderCurrentRound();
    if (window.app && window.app.syncScanSessions) window.app.syncScanSessions();

    // Said out loud, and only once per group: the entry link the arbiter has been
    // handing out has just stopped working, and finding that out from a confused
    // player is worse than one dialog.
    if (closedName) {
        await window.Dialog.warn(
            tr('Online registration for {name} is now closed, because the first round has been paired. You can reopen it under Options if you still want entries.', { name: closedName }),
            tr('Registration Closed')
        );
    }
}

export async function undoLastRound() {
    const roundNo = state.rounds.length;
    if (roundNo === 0) return;

    const confirmed = await window.Dialog.confirm(tr('Are you sure you want to delete Round {n}? All results for this round will be lost.', { n: roundNo }), tr('Undo Round'), tr('Delete'), true);
    if (confirmed) {
        state.rounds.pop();
        state.viewedRoundIndex = Math.max(0, state.rounds.length - 1);
        bumpStructureRev();
        saveToLocalStorage();
        updateUIState();
        renderCurrentRound();
        renderStandings();
        await window.Dialog.success(tr('Round {n} has been removed.', { n: roundNo }), tr('Round Removed'));
    }
}

function selectNextAvailableBoard() {
    if (registerResultsState.length === 0) return;
    
    let nextIdx = (selectedBoardIdx + 1) % registerResultsState.length;
    for (let i = 0; i < registerResultsState.length; i++) {
        const board = registerResultsState[nextIdx];
        if (!board.result) {
            selectedBoardIdx = nextIdx;
            return;
        }
        nextIdx = (nextIdx + 1) % registerResultsState.length;
    }
}

// The boards list is its own scroller (max-height + overflow-y). Keep the
// selected board visible as hotkeys/dropdowns walk down the list.
let lastScrolledBoardIdx = -1;
let boardScrollAnim = null;

function scrollSelectedBoardIntoView() {
    if (selectedBoardIdx === lastScrolledBoardIdx) return;
    lastScrolledBoardIdx = selectedBoardIdx;

    const body = document.getElementById('register-results-body');
    if (!body || selectedBoardIdx < 0) return;
    const row = body.querySelector(`.register-board-row[data-index="${selectedBoardIdx}"]`);
    if (!row) return;
    scrollBoardIntoView(body, row);
}

// Scroll `body` just far enough to show `row` whole. Native smooth scrolling
// silently no-ops in some browsers, so animate scrollTop.
function scrollBoardIntoView(body, row) {
    const margin = 8;
    const bodyRect = body.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();

    let delta = 0;
    if (rowRect.top < bodyRect.top + margin) {
        delta = rowRect.top - bodyRect.top - margin;
    } else if (rowRect.bottom > bodyRect.bottom - margin) {
        delta = rowRect.bottom - bodyRect.bottom + margin;
    }
    if (!delta) return;

    const maxScroll = Math.max(0, body.scrollHeight - body.clientHeight);
    const from = body.scrollTop;
    const to = Math.min(Math.max(from + delta, 0), maxScroll);
    if (to === from) return;

    if (boardScrollAnim) cancelAnimationFrame(boardScrollAnim);
    const duration = 180;
    const start = performance.now();
    const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        body.scrollTop = from + (to - from) * eased;
        if (t < 1) {
            boardScrollAnim = requestAnimationFrame(step);
        } else {
            boardScrollAnim = null;
        }
    };
    boardScrollAnim = requestAnimationFrame(step);
}

export function renderRegisterResults() {
    const body = document.getElementById('register-results-body');
    if (!body) return;

    if (registerResultsState.length === 0) {
        body.innerHTML = `<p class="text-center" style="padding: 2rem; color: var(--text-muted);">${tr('No pairings in this round.')}</p>`;
        return;
    }

    const searchInput = document.getElementById('register-results-search');
    const query = searchInput ? searchInput.value.toLowerCase() : '';

    const filteredIndices = [];
    registerResultsState.forEach((p, index) => {
        if (!query) {
            filteredIndices.push(index);
            return;
        }

        const boardNum = (index + 1).toString();
        const white = state.players.find(pl => pl.id === p.white);
        const black = (p.black === 'BYE' || p.black === 'WO') ? { lastName: p.black, givenName: '', id: p.black } : state.players.find(pl => pl.id === p.black);
        const whiteName = white ? (white.lastName + " " + white.givenName).toLowerCase() : '';
        const blackName = black ? (black.lastName + " " + black.givenName).toLowerCase() : '';

        if (boardNum.includes(query) || whiteName.includes(query) || blackName.includes(query)) {
            filteredIndices.push(index);
        }
    });

    if (filteredIndices.length === 0) {
        body.innerHTML = `<p class="text-center" style="padding: 2rem; color: var(--text-muted);">${tr('No boards match your search.')}</p>`;
        return;
    }

    body.innerHTML = filteredIndices.map(idx => {
        const p = registerResultsState[idx];
        const white = state.players.find(pl => pl.id === p.white);
        const black = (p.black === 'BYE' || p.black === 'WO') ? { lastName: p.black, givenName: '', id: p.black } : state.players.find(pl => pl.id === p.black);
        
        const isSelected = idx === selectedBoardIdx;
        
        const rowStyle = isSelected
            ? `border: 2px solid var(--primary); background: rgba(59, 130, 246, 0.12); box-shadow: 0 0 10px rgba(59, 130, 246, 0.25); transform: translateY(-1px);`
            : `border: 1px solid var(--border-color); background: var(--surface-tint-faint);`;

        return `
            <div class="register-board-row" data-index="${idx}" style="display: flex; align-items: center; padding: 0.35rem 0.6rem; border-radius: 6px; cursor: pointer; transition: all 0.2s; ${rowStyle}">
                <div style="display: flex; align-items: center; gap: 1rem; flex: 1; min-width: 0;">
                    <div style="font-weight: 700; color: var(--text-muted); font-size: 0.85rem; min-width: 30px;">#${idx + 1}</div>
                    <div style="display: grid; grid-template-columns: 1fr auto 1fr; gap: 1rem; align-items: center; flex: 1; min-width: 0;">
                        <!-- White -->
                        <div style="font-weight: 600; text-align: right; color: var(--text-main); font-size: 0.85rem; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;">
                            ${formatPlayerName(white, true)}
                        </div>
                        <!-- Center Dropdown Selector -->
                        <div class="register-board-pick" style="text-align: center; min-width: 100px; flex-shrink: 0;">
                            <select class="form-control register-board-select" data-index="${idx}" style="margin: 0; width: 100px; height: 28px; padding: 0 4px; font-size: 0.8rem; border-radius: 6px; text-align-last: center; text-align: center; font-weight: 700; background: var(--input-bg); color: var(--text-main); border: 1px solid var(--border-color); cursor: pointer;">
                                <option value="" ${!p.result ? 'selected' : ''}>---</option>
                                <option value="1-0" ${p.result === '1-0' ? 'selected' : ''}>1 - 0</option>
                                <option value="0.5-0.5" ${p.result === '0.5-0.5' ? 'selected' : ''}>½ - ½</option>
                                <option value="0-1" ${p.result === '0-1' ? 'selected' : ''}>0 - 1</option>
                                <option value="+/-" ${p.result === '+/-' ? 'selected' : ''}>+ / -</option>
                                <option value="-/+" ${p.result === '-/+' ? 'selected' : ''}>- / +</option>
                                <option value="-/-" ${p.result === '-/-' ? 'selected' : ''}>- / -</option>
                            </select>
                        </div>
                        <!-- Black -->
                        <div style="font-weight: 600; text-align: left; color: var(--text-main); font-size: 0.85rem; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;">
                            ${formatPlayerName(black, true)}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }).join('');

    body.querySelectorAll('.register-board-row').forEach(row => {
        row.addEventListener('click', (e) => {
            // A click on the result dropdown is the dropdown's: selecting the row
            // re-renders the list, which would close the menu as it opens.
            if (e.target.closest('.register-board-pick')) return;
            selectedBoardIdx = parseInt(row.dataset.index);
            renderRegisterResults();
        });
    });

    body.querySelectorAll('.register-board-select').forEach(select => {
        select.addEventListener('change', (e) => {
            const idx = parseInt(select.dataset.index);
            const val = select.value;
            setResult(registerResultsState[idx], val);
            
            if (val) {
                selectedBoardIdx = idx;
                selectNextAvailableBoard();
            } else {
                selectedBoardIdx = idx;
            }
            renderRegisterResults();
        });
    });

    scrollSelectedBoardIntoView();
}

export function openRegisterResultsModal() {
    if (!state.rounds || state.rounds.length === 0 || state.viewedRoundIndex >= state.rounds.length) {
        window.Dialog.error(tr('Generate a round first before registering results.'), tr('Error'));
        return;
    }

    // Re-opening a modal that is ALREADY open must not reload the round over the
    // top of what the arbiter has typed: everything entered here is unsaved until
    // Save Results, so re-seeding from state.rounds would throw it all away. The
    // modal is left exactly as it stands.
    const modalAlreadyOpen = !!document.getElementById('register-results-modal')?.classList.contains('active');
    if (modalAlreadyOpen) return;

    const round = state.rounds[state.viewedRoundIndex];
    registerResultsState = JSON.parse(JSON.stringify(round.pairings));

    selectedBoardIdx = registerResultsState.findIndex(p => !p.result);
    if (selectedBoardIdx === -1) {
        selectedBoardIdx = 0;
    }

    const searchInput = document.getElementById('register-results-search');
    if (searchInput) {
        searchInput.value = '';
        if (!searchInput.dataset.hasListener) {
            searchInput.addEventListener('input', () => {
                renderRegisterResults();
            });
            searchInput.dataset.hasListener = 'true';
        }
    }

    renderRegisterResults();
    updateHotkeyBadges();

    const modal = document.getElementById('register-results-modal');
    if (modal) {
        modal.classList.add('active');
        // Rows have no layout until the modal is active, so scroll the first
        // unplayed board into view once it does.
        lastScrolledBoardIdx = -1;
        requestAnimationFrame(() => scrollSelectedBoardIntoView());
    }

    if (!isRegisterResultsInitialized) {
        const container = document.getElementById('register-results-buttons-container');
        if (container) {
            container.querySelectorAll('.btn-result-register').forEach(button => {
                button.addEventListener('click', () => {
                    const resultVal = button.dataset.result;
                    if (selectedBoardIdx >= 0 && selectedBoardIdx < registerResultsState.length) {
                        setResult(registerResultsState[selectedBoardIdx], resultVal);
                        selectNextAvailableBoard();
                        renderRegisterResults();
                    }
                });
            });
        }

        const btnDelete = document.getElementById('btn-result-delete');
        if (btnDelete) {
            btnDelete.addEventListener('click', () => {
                if (selectedBoardIdx >= 0 && selectedBoardIdx < registerResultsState.length) {
                    setResult(registerResultsState[selectedBoardIdx], '');
                    renderRegisterResults();
                }
            });
        }

        const btnEditHotkeys = document.getElementById('btn-edit-hotkeys');
        if (btnEditHotkeys) {
            btnEditHotkeys.addEventListener('click', () => {
                openEditHotkeysModal();
            });
        }

        const btnSaveHotkeys = document.getElementById('btn-save-hotkeys');
        if (btnSaveHotkeys) {
            btnSaveHotkeys.addEventListener('click', () => {
                hotkeysState = { ...tempHotkeys };
                saveHotkeys();
                updateHotkeyBadges();
                const hotkeysModal = document.getElementById('edit-hotkeys-modal');
                if (hotkeysModal) {
                    hotkeysModal.classList.remove('active');
                }
            });
        }

        const btnResetHotkeys = document.getElementById('btn-reset-hotkeys');
        if (btnResetHotkeys) {
            btnResetHotkeys.addEventListener('click', () => {
                tempHotkeys = { ...DEFAULT_HOTKEYS };
                recordingAction = null;
                renderHotkeysList();
            });
        }

        isRegisterResultsInitialized = true;
    }
}

export async function saveRegisterResults() {
    if (state.isSpectatorMode || !state.rounds || state.rounds.length === 0) return;

    state.rounds[state.viewedRoundIndex].pairings = registerResultsState;
    saveToLocalStorage();
    renderCurrentRound();
    renderStandings();
    updateUIState();
    if (window.app && window.app.syncScanSessions) window.app.syncScanSessions();

    const modal = document.getElementById('register-results-modal');
    if (modal) {
        modal.classList.remove('active');
    }
}

export function openEditPairingsModal() {
    if (!state.rounds || state.rounds.length === 0 || state.viewedRoundIndex >= state.rounds.length) {
        window.Dialog.error(tr('Generate a round first before editing pairings.'), tr('Error'));
        return;
    }
    if (editPairingsSearch) editPairingsSearch.value = '';
    const round = state.rounds[state.viewedRoundIndex];
    editPairingsState = JSON.parse(JSON.stringify(round.pairings));
    editPairingsOriginal = new Set(round.pairings.map(p => `${p.white}|${p.black}`));
    renderEditPairings();
    editPairingsModal.classList.add('active');
}

export function addPairingBoard() {
    editPairingsState.push({ white: '', black: '', result: '' });
    // A search would hide the new board: it has no players yet to match.
    if (editPairingsSearch) editPairingsSearch.value = '';
    renderEditPairings();
    // It goes in at the bottom of the list, below the fold of any sizeable round,
    // and nothing on screen changed — so take the arbiter to it.
    const body = document.getElementById('edit-pairings-body');
    const scroller = body && body.closest('.modal-body');
    const added = body && body.querySelector(`.edit-pairing-board[data-board="${editPairingsState.length - 1}"]`);
    if (scroller && added) scrollBoardIntoView(scroller, added);
}

export function renderEditPairings() {
    const body = document.getElementById('edit-pairings-body');
    const query = (editPairingsSearch ? editPairingsSearch.value : '').toLowerCase();

    const pairedIds = new Set();
    editPairingsState.forEach(p => {
        if (p.white && p.white !== 'BYE' && p.white !== 'WO') pairedIds.add(p.white);
        if (p.black && p.black !== 'BYE' && p.black !== 'WO') pairedIds.add(p.black);
    });

    const unpairedPlayers = state.players.filter(p => !pairedIds.has(p.id) && p.status);
    const absentPlayers = state.players.filter(p => !pairedIds.has(p.id) && !p.status);

    const generateOptions = (selectedValue, currentOpponentValue) => {
        let opts = `<option value="">-- None --</option>`;
        opts += `<option value="WO" ${selectedValue === 'WO' || selectedValue === 'BYE' ? 'selected' : ''}>WO</option>`;

        // Present players without a board come first: they are who a board is
        // usually added or changed for, and below the paired players they sat at
        // the end of a list as long as the round.
        opts += `<optgroup label="${tr('Unpaired Players (Present)')}">`;
        unpairedPlayers.forEach(p => {
            opts += `<option value="${escapeHtml(p.id)}">${escapeHtml(formatPlayerName(p))} (${escapeHtml(p.rating || 'UR')})</option>`;
        });
        opts += `</optgroup>`;

        opts += `<optgroup label="${tr('Paired Players')}">`;
        state.players.filter(p => pairedIds.has(p.id)).forEach(p => {
            if (p.id === currentOpponentValue) return;
            opts += `<option value="${escapeHtml(p.id)}" ${selectedValue === p.id ? 'selected' : ''}>${escapeHtml(formatPlayerName(p))} (${escapeHtml(p.rating || 'UR')})</option>`;
        });
        opts += `</optgroup>`;

        if (absentPlayers.length > 0) {
            opts += `<optgroup label="${tr('Absent Players')}">`;
            absentPlayers.forEach(p => {
                opts += `<option value="${escapeHtml(p.id)}">${escapeHtml(formatPlayerName(p))} (${escapeHtml(p.rating || 'UR')})</option>`;
            });
            opts += `</optgroup>`;
        }

        return opts;
    };

    if (editPairingsState.length === 0) {
        body.innerHTML = `<p class="text-center" style="padding: 2rem; color: var(--text-muted);">${tr('No boards. Click "Add Board" below.')}</p>`;
    } else {
        const filteredIndices = [];
        editPairingsState.forEach((p, index) => {
            if (!query) {
                filteredIndices.push(index);
                return;
            }

            const boardNum = (index + 1).toString();
            const white = state.players.find(pl => pl.id === p.white);
            const black = state.players.find(pl => pl.id === p.black);
            const whiteName = white ? (white.lastName + " " + white.givenName).toLowerCase() : '';
            const blackName = black ? (black.lastName + " " + black.givenName).toLowerCase() : '';

            if (boardNum.includes(query) || whiteName.includes(query) || blackName.includes(query)) {
                filteredIndices.push(index);
            }
        });

        if (filteredIndices.length === 0) {
            body.innerHTML = `<p class="text-center" style="padding: 2rem; color: var(--text-muted);">${tr('No boards matching your search.')}</p>`;
            return;
        }

        body.innerHTML = filteredIndices.map(index => {
            const p = editPairingsState[index];
            const forbiddenRound = checkForbiddenPairing(p.white, p.black, state.viewedRoundIndex);

            return `
            <div class="edit-pairing-board" data-board="${index}" style="margin-bottom: 0.75rem;">
                <div style="display: flex; gap: 0.75rem; align-items: flex-end; background: var(--surface-tint-faint); padding: 0.5rem 0.75rem; border-radius: 12px; border: 1px solid ${forbiddenRound ? 'var(--danger)' : 'var(--border-color)'};">
                    <div style="display: flex; flex-direction: column; flex: 1;">
                        <label style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); margin-bottom: 0.25rem; font-weight: 600;">${tr('White (Board {n})', { n: index + 1 })}</label>
                        <select class="form-control pairing-edit-select" data-board="${index}" data-color="white" style="padding: 0.5rem; height: 38px;">
                            ${generateOptions(p.white, p.black)}
                        </select>
                    </div>
                    <div style="width: 24px; text-align: center; font-weight: bold; color: var(--text-muted); margin-bottom: 0.6rem; font-size: 0.8rem;">VS</div>
                    <div style="display: flex; flex-direction: column; flex: 1;">
                        <label style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); margin-bottom: 0.25rem; font-weight: 600;">${tr('Black (Board {n})', { n: index + 1 })}</label>
                        <select class="form-control pairing-edit-select" data-board="${index}" data-color="black" style="padding: 0.5rem; height: 38px;">
                            ${generateOptions(p.black, p.white)}
                        </select>
                    </div>
                    <button class="btn-icon-small btn-icon-danger btn-delete-pairing" data-board="${index}" style="flex-shrink: 0; width: 38px; height: 38px; background: var(--danger-soft); border: 1px solid var(--danger-border); border-radius: 10px; display: flex; align-items: center; justify-content: center; transition: all 0.2s;">
                        <i class="ri-delete-bin-line" style="font-size: 1.1rem;"></i>
                    </button>
                </div>
                ${forbiddenRound ? `<div style="color: var(--danger); font-size: 0.75rem; padding: 0.25rem 0.5rem; font-weight: 600;"><i class="ri-error-warning-line"></i> ${tr('Already played in Round {n}', { n: forbiddenRound })}</div>` : ''}
            </div>
        `;
        }).join('');

        body.querySelectorAll('.pairing-edit-select').forEach(sel => {
            sel.addEventListener('change', (e) => {
                const boardIndex = parseInt(e.target.dataset.board);
                const color = e.target.dataset.color;
                const newValue = e.target.value;

                // Boards nobody has been seated at yet — added with Add Board — stay. Only
                // a board this change leaves empty goes: its one player moved to another
                // board, or taken off it. Clearing every empty board here took the other
                // new boards with it as soon as the first was given a player.
                const emptyBefore = new Set(editPairingsState.filter(b => !b.white && !b.black));

                if (newValue && newValue !== 'BYE' && newValue !== 'WO') {
                    editPairingsState.forEach((board) => {
                        if (board.white === newValue) board.white = '';
                        if (board.black === newValue) board.black = '';
                    });
                }

                editPairingsState[boardIndex][color] = newValue;
                editPairingsState = editPairingsState.filter(b => b.white || b.black || emptyBefore.has(b));
                renderEditPairings();
            });
        });

        body.querySelectorAll('.btn-delete-pairing').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const boardIndex = parseInt(e.currentTarget.dataset.board);
                editPairingsState.splice(boardIndex, 1);
                renderEditPairings();
            });
        });
    }
}

export async function saveEditedPairings() {
    if (state.isSpectatorMode) return;
    let validBoards = [];
    let hasIncomplete = false;
    let forbiddenPairs = [];

    for (let i = 0; i < editPairingsState.length; i++) {
        const b = editPairingsState[i];

        const forbiddenRound = checkForbiddenPairing(b.white, b.black, state.viewedRoundIndex);
        if (forbiddenRound) {
            const p1 = state.players.find(p => p.id === b.white);
            const p2 = state.players.find(p => p.id === b.black);
            forbiddenPairs.push(`${formatPlayerName(p1)} vs ${formatPlayerName(p2)} (played in R${forbiddenRound})`);
        }
        if ((!b.white && b.black) || (b.white && !b.black)) {
            hasIncomplete = true;
        }
        if (b.white && b.black) {
            b.result = editedBoardResult(b, editPairingsOriginal.has(`${b.white}|${b.black}`));
            validBoards.push(b);
        }
    }

    if (hasIncomplete) {
        const confirmed = await window.Dialog.confirm(tr('Some boards have only one player. These boards will be deleted and the players will be marked as unpaired. Continue?'), tr('Incomplete Boards'), tr('Continue'), true);
        if (!confirmed) return;
    }

    if (forbiddenPairs.length > 0) {
        const message = tr('The following forbidden pairings (players who have already met) were found:') + "\n\n" +
            forbiddenPairs.join("\n") +
            "\n\n" + tr('Are you sure you want to save these pairings?');
        const confirmed = await window.Dialog.confirm(message, tr('Forbidden Pairings Found'), tr('Save Anyway'), false, undefined, 'warning');
        if (!confirmed) return;
    }

    if (validBoards && validBoards.length > 0) {
        validBoards.sort((a, b) => {
            const aIsWO = a.white === 'WO' || a.black === 'WO' || a.white === 'BYE' || a.black === 'BYE';
            const bIsWO = b.white === 'WO' || b.black === 'WO' || b.white === 'BYE' || b.black === 'BYE';
            if (aIsWO && !bIsWO) return 1;
            if (!aIsWO && bIsWO) return -1;
            return 0;
        });
    }

    state.rounds[state.viewedRoundIndex].pairings = validBoards;
    bumpStructureRev();
    saveToLocalStorage();
    renderCurrentRound();
    renderStandings();
    if (window.app && window.app.syncScanSessions) window.app.syncScanSessions();
    editPairingsModal.classList.remove('active');
}